import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, NavLink, useNavigate, useParams } from 'react-router-dom';
import { authApi } from './lib/api';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Shops } from './pages/Shops';
import { ShopDetail } from './pages/ShopDetail';
import { AddShop } from './pages/AddShop';
import { ImportCSV } from './pages/ImportCSV';
import { Invitations } from './pages/Invitations';
import { Tickets, TicketDetail } from './pages/Tickets';

interface AuthState {
  loading: boolean;
  user: { id: string; email: string; role: string } | null;
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ loading: true, user: null });

  useEffect(() => {
    authApi
      .me()
      .then((res) => setAuth({ loading: false, user: res.user }))
      .catch(() => setAuth({ loading: false, user: null }));
  }, []);

  if (auth.loading) {
    return <div style={{ padding: 32 }}>Chargement…</div>;
  }

  if (!auth.user || auth.user.role !== 'admin') {
    return (
      <Routes>
        <Route
          path="/login"
          element={<Login onAuth={(u) => setAuth({ loading: false, user: u })} />}
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return <AppShell user={auth.user} onLogout={() => setAuth({ loading: false, user: null })} />;
}

function AppShell({
  user,
  onLogout,
}: {
  user: { id: string; email: string; role: string };
  onLogout: () => void;
}) {
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    onLogout();
    navigate('/login');
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        <h1>Reims Local · Admin</h1>
        <nav>
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
            Dashboard
          </NavLink>
          <NavLink to="/shops" className={({ isActive }) => (isActive ? 'active' : '')}>
            Boutiques
          </NavLink>
          <NavLink to="/shops/new" className={({ isActive }) => (isActive ? 'active' : '')}>
            Ajouter une boutique
          </NavLink>
          <NavLink to="/import" className={({ isActive }) => (isActive ? 'active' : '')}>
            Import CSV
          </NavLink>
          <NavLink to="/invitations" className={({ isActive }) => (isActive ? 'active' : '')}>
            Invitations
          </NavLink>
          <NavLink to="/tickets" className={({ isActive }) => (isActive ? 'active' : '')}>
            Tickets support
          </NavLink>
        </nav>
        <div style={{ marginTop: 32, fontSize: 13, color: 'var(--muted)' }}>
          <div>{user.email}</div>
          <button style={{ marginTop: 10, width: '100%' }} onClick={handleLogout}>
            Se déconnecter
          </button>
        </div>
      </aside>
      <main className="main">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/shops" element={<Shops />} />
          <Route path="/shops/new" element={<AddShop />} />
          <Route path="/shops/:id" element={<ShopDetail />} />
          <Route path="/import" element={<ImportCSV />} />
          <Route path="/invitations" element={<Invitations />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/tickets/:id" element={<TicketDetailRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function TicketDetailRoute() {
  const params = useParams<{ id: string }>();
  if (!params.id) return null;
  return <TicketDetail ticketId={params.id} />;
}
